// Static tier table for the landing page (mirrors the backend PLAN_CATALOG
// plus the informational LOCAL tier included with hardware).
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import { Cpu, Sparkles } from "../../components/icons";
import { PlanIcon, type PlanIconKey } from "../../components/PlanIcon";
import { DEFAULT_REGION, money, region } from "../../config/finance";
import { track } from "../../lib/analytics";

interface Tier {
  key: string;
  nameKey: string;
  price: string | null; // null → i18n "included with hardware"
  perSite?: boolean;
  from?: boolean;
  highlight?: boolean;
  featureKeys: string[];
  comingSoonKeys?: string[];
  baseKey?: string; // "everything in X" line
}

const TIERS: Tier[] = [
  {
    key: "local",
    nameKey: "tiers.local",
    price: null,
    featureKeys: [
      "features.local_dashboard",
      "features.local_api",
      "features.local_mqtt",
      "features.local_shelly",
      "features.local_relays",
      "features.local_backup",
      "features.local_history",
      "features.local_nocloud",
    ],
  },
  {
    key: "free",
    nameKey: "tiers.free",
    price: "€0",
    featureKeys: [
      "features.monitor",
      "features.settings_backup",
      "features.one_site",
      "features.device_health",
    ],
  },
  {
    key: "basic",
    nameKey: "tiers.basic",
    price: "€5",
    baseKey: "tiers.free",
    featureKeys: [
      "features.three_sites",
      "features.remote_admin",
      "features.share_site",
      "features.data_backup",
      "features.extended_history",
      "features.alerts",
      "features.export",
    ],
  },
  {
    key: "pro",
    nameKey: "tiers.pro",
    price: "€20",
    highlight: true,
    baseKey: "tiers.basic",
    featureKeys: [
      "features.entsoe_prices",
      "features.optimization_insights",
      "features.grid_power",
      "features.pv_forecast",
      "features.planned_vs_actual",
      "features.incident_reports",
      "features.daily_analysis",
      "features.monthly_savings",
      "features.suggestions",
    ],
  },
  {
    key: "fleet",
    nameKey: "tiers.fleet",
    price: "€30",
    perSite: true,
    from: true,
    baseKey: "tiers.pro",
    featureKeys: [
      "features.unlimited_sites",
      "features.fleet_dashboard",
      "features.roles",
      "features.remote_diagnostics",
      "features.fleet_reporting",
      "features.daily_fleet_analysis",
      "features.api_access",
    ],
    comingSoonKeys: ["features.fleet_optimization", "features.config_templates"],
  },
];

export function PricingSection() {
  const { t } = useTranslation();
  // The landing page has no region selector, so hardware is quoted in the
  // default region's currency; the calculator below re-prices per region.
  const home = region(DEFAULT_REGION);
  return (
    <section id="pricing" className="max-w-6xl mx-auto px-4 py-16">
      <h2 className="text-2xl md:text-3xl font-semibold text-center">{t("landing.pricingTitle")}</h2>
      <p className="text-muted text-center mt-2 max-w-2xl mx-auto">{t("landing.pricingSub")}</p>
      <p className="text-sm text-center mt-3 max-w-2xl mx-auto">
        <span className="font-medium">{t("landing.pricingHardware")}</span>{" "}
        <span className="text-muted">{t("landing.pricingCosts")}</span>
      </p>
      {/* One-time hardware cost, shown next to the recurring plans so the two
          are never confused. Figures come from config/finance.json. */}
      <div className="mt-6 card p-5 max-w-2xl mx-auto">
        <div className="flex items-center gap-2 font-medium">
          <Cpu size={16} />
          {t("landing.hardwareTitle", { defaultValue: "One-time hardware cost" })}
        </div>
        <dl className="mt-3 space-y-2 text-sm">
          <div className="flex items-baseline justify-between gap-4">
            <dt>{t("landing.hardwareDevice", { defaultValue: "Complete Inverter hardware" })}</dt>
            <dd className="font-mono font-semibold whitespace-nowrap">
              {money(home, home.hardwarePrice)}
              <span className="text-xs text-muted font-sans"> {t("landing.exVat", { defaultValue: "+ VAT" })}</span>
            </dd>
          </div>
          <div className="flex items-baseline justify-between gap-4">
            <dt>{t("landing.hardwareInstall", { defaultValue: "Standard installation" })}</dt>
            <dd className="font-mono font-semibold whitespace-nowrap">
              {home.installationFrom && (
                <span className="text-xs text-muted font-sans">{t("landing.fromPrice")} </span>
              )}
              {money(home, home.installationPrice)}
              <span className="text-xs text-muted font-sans"> {t("landing.exVat", { defaultValue: "+ VAT" })}</span>
            </dd>
          </div>
        </dl>
        <p className="text-xs text-muted mt-3">
          {t("landing.hardwareNote", {
            defaultValue:
              "Installation is a starting price — the final quote depends on your inverter, wiring and location.",
          })}
        </p>
      </div>
      <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        {TIERS.map((tier) => (
          <div
            key={tier.key}
            className={`card p-5 flex flex-col ${tier.highlight ? "ring-2 ring-brand border-brand" : ""}`}
          >
            {/* 64px plan mark, single colour: the highlighted tier takes the
                brand accent, the rest stay muted so one card leads. */}
            <PlanIcon
              plan={tier.key as PlanIconKey}
              size={64}
              className={`mb-3 ${tier.highlight ? "text-brand" : "text-muted"}`}
            />
            <div className="flex items-center justify-between">
              <h3 className="font-semibold flex items-center gap-1.5">
                <PlanIcon plan={tier.key as PlanIconKey} size={14} className="text-muted" />
                {t(tier.nameKey)}
              </h3>
              {tier.highlight && (
                <span className="chip text-brand border-brand/40 bg-brand/10">
                  <Sparkles size={12} /> Pro
                </span>
              )}
            </div>
            <div className="mt-3 min-h-[3.5rem]">
              {tier.price === null ? (
                <div className="text-sm text-muted">{t("tiers.localPrice")}</div>
              ) : (
                <>
                  <div className="text-3xl font-semibold font-mono">
                    {tier.from && <span className="text-sm font-sans text-muted mr-1">{t("landing.fromPrice")}</span>}
                    {tier.price}
                  </div>
                  <div className="text-xs text-muted">
                    {tier.perSite ? t("landing.perSiteMonth") : t("common.perMonth")}
                  </div>
                </>
              )}
            </div>
            <ul className="mt-4 space-y-2 text-sm flex-1">
              {tier.baseKey && (
                <li className="text-muted italic">
                  {t("landing.everythingIn", { tier: t(tier.baseKey) })}
                </li>
              )}
              {tier.featureKeys.map((key) => (
                <li key={key} className="flex items-start gap-2">
                  <Check size={14} className="text-good mt-0.5 shrink-0" />
                  <span>{t(key)}</span>
                </li>
              ))}
              {tier.comingSoonKeys?.map((key) => (
                <li key={key} className="flex items-start gap-2 text-muted">
                  <Sparkles size={14} className="mt-0.5 shrink-0" />
                  <span>
                    {t(key)} · {t("common.comingSoon")}
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-5">
              {tier.key === "local" ? (
                <span className="chip">{t("common.included")}</span>
              ) : (
                <Link
                  to="/register"
                  className={tier.highlight ? "btn-primary w-full justify-center" : "btn-ghost w-full justify-center"}
                  onClick={() => track("plan_selected", { plan: tier.key })}
                >
                  {tier.key === "free" ? t("nav.getStarted") : t("subscription.choose")}
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted text-center mt-8 max-w-3xl mx-auto">{t("landing.securityNote")}</p>
    </section>
  );
}
